import React, { Context, createContext } from 'react';
import { useDispatch } from 'react-redux';
import Box from '@mui/material/Box';
import { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { OpenAPIV3_1 as OpenAPI } from "openapi-types";
import _, { Dictionary } from 'lodash';
import Get from './methods/Get';
import Post from './methods/Post';
import Put from './methods/Put';
import Delete from './methods/Delete';
import Patch from './methods/Patch';
import MethodAccordion from './MethodAccordion';

export interface Path {
    pathKey: string;
    pathItemObject: OpenAPI.PathItemObject;
}

export const PathContext: Context<Path | null> = createContext<Path | null>(null);

const Header: React.FC<{
    title: string;
    pathKey: string;
    operationObject: OpenAPI.OperationObject,
    color: (theme: Theme) => string;
}> = ({ title, pathKey, operationObject, color }) => {
    const { summary } = operationObject;
    return (
        <Box sx={{ display: 'flex', flexGrow: 1 }}>
            <Typography sx={{
                color: theme => color(theme),
                fontWeight: 600,
                marginRight: 1,
                width: '4em',
                border: theme => `2px solid ${color(theme)}`,
                textAlign: 'center',
            }}>{title}</Typography>
            <Typography>{pathKey}</Typography>
            <Typography sx={{ flexGrow: 1, textAlign: 'right' }}>{summary}</Typography>
        </Box>
    );
};

export interface MethodsProps {
    pathKey: string;
    pathItemObject: OpenAPI.PathItemObject;
}

const Methods: React.FC<MethodsProps> = ({ pathKey, pathItemObject }) => {
    const [expanded, setExpanded] = React.useState<string[]>([]);
    const dispatch = useDispatch();

    const toggle = (key: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
        if (newExpanded) {
            setExpanded([...expanded, key]);
        } else {
            const modified = [...expanded];
            modified.splice(modified.indexOf(key), 1);
            setExpanded(modified);
        }
    }


    return (
        <PathContext.Provider value={{ pathKey, pathItemObject }}>
            <Box>
                {
                    pathItemObject.get !== undefined && <MethodAccordion
                        header={
                            <Header
                                title={'GET'}
                                pathKey={pathKey}
                                color={theme => theme.palette.success.main}
                                operationObject={pathItemObject.get}
                            />
                        }
                        pathKey={pathKey
                        }
                        open={expanded.includes('get-' + pathKey)}
                        toggle={toggle('get-' + pathKey)}
                        method={'GET'}
                    >
                        <Get operationObject={pathItemObject.get} />
                    </MethodAccordion>
                }
                {
                    pathItemObject.post !== undefined && <MethodAccordion
                        header={
                            <Header
                                title={'POST'}
                                pathKey={pathKey}
                                color={theme => theme.palette.primary.main}
                                operationObject={pathItemObject.post}
                            />
                        }
                        pathKey={pathKey}
                        open={expanded.includes('post-' + pathKey)}
                        toggle={toggle('post-' + pathKey)}
                        method={'POST'}
                    >
                        <Post operationObject={pathItemObject.post} />
                    </MethodAccordion>
                }
                {
                    pathItemObject.put !== undefined && <MethodAccordion
                        header={
                            <Header
                                title={'PUT'}
                                pathKey={pathKey}
                                color={theme => theme.palette.secondary.main}
                                operationObject={pathItemObject.put}
                            />
                        }
                        pathKey={pathKey}
                        open={expanded.includes('put-' + pathKey)}
                        toggle={toggle('put-' + pathKey)}
                        method={'PUT'}
                    >
                        <Put operationObject={pathItemObject.put} />
                    </MethodAccordion>
                }
                {
                    pathItemObject.delete !== undefined && <MethodAccordion
                        header={
                            <Header
                                title={'DELETE'}
                                pathKey={pathKey}
                                color={theme => theme.palette.error.main}
                                operationObject={pathItemObject.delete}
                            />
                        }
                        pathKey={pathKey}
                        open={expanded.includes('delete-' + pathKey)}
                        toggle={toggle('delete-' + pathKey)}
                        method={'DELETE'}
                    >
                        <Delete operationObject={pathItemObject.delete} />
                    </MethodAccordion>
                }
                {
                    pathItemObject.patch !== undefined && <MethodAccordion
                        header={
                            <Header
                                title={'PATCH'}
                                pathKey={pathKey}
                                color={theme => theme.palette.warning.main}
                                operationObject={pathItemObject.patch}
                            />
                        }
                        pathKey={pathKey}
                        open={expanded.includes('patch-' + pathKey)}
                        toggle={toggle('patch-' + pathKey)}
                        method={'PATCH'}
                    >
                        <Patch operationObject={pathItemObject.patch} />
                    </MethodAccordion>
                }
            </Box>
        </PathContext.Provider>
    );
};

export default Methods;
